import {
  computed,
  effect,
  Injector,
  isSignal,
  Signal,
  signal,
  untracked,
} from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SseDataAccess } from '../data-access/sse.data-access';
import { SseLocation } from './types/sse-location';
import { HandlerRef } from './types/handler-ref';
import { EventName } from 'fabric/fabric-impl';

export type EventHandler<Payload> = (payload: Payload) => void;

export type EventHandlerFactory<Payload> = () => EventHandler<Payload>;

export type EventHandlerDefinition<T extends Record<string, unknown>> = {
  [EventName in keyof T]: EventHandlerFactory<T[EventName]>;
};

export interface SseResourceInitOptions<EventDefObject extends Record<string, unknown>> {
  name?: string;

  /**
   * The events which the resource can handle (see `sse()`)
   * We explicitly define the events to prevent mistakes
   */
  events: EventHandlerDefinition<EventDefObject>;

  /**
   * When specified, the resource auto-connects to the provided location.
   * If the provided location is a signal (or function with signals), it connects as soon as the value is not `undefined`
   * Whenever the value changes it disconnects from the previous location and connects to the new one
   */
  location?:
    | SseLocation
    | string
    | (() => SseLocation | string | undefined)
    | Signal<SseLocation | string | undefined>;

  keepAlive?: boolean;
  reconnectDelay?: number;
}

export interface SseResourceHandleOptions {
  active?: () => boolean | Signal<boolean>;
  injector?: Injector;
}

export type EventHandlers<T extends Record<string, unknown>> = {
  [EventName in keyof T]: EventHandler<T[EventName]>;
};

/**
 * Object for managing Event Handlers and executing them when an SSE is received
 * Handlers can be registered in two different ways. Choose depending on the use case.
 */
export class SseResource<EventDefObject extends Record<string, unknown>> {
  private readonly eventMap: Map<
    keyof SseResourceInitOptions<EventDefObject>['events'],
    Set<EventHandler<unknown>>
  > = new Map();

  readonly connected = signal(false);
  readonly messages$ = new BehaviorSubject<any[]>([]);

  private subscription?: Subscription;

  constructor(
    protected init: SseResourceInitOptions<EventDefObject>,
    protected readonly sseDataAccess: SseDataAccess,
    protected readonly injector: Injector
  ) {
    for (const eventName in init.events) {
      const eventHandlersSet = new Set<EventHandler<unknown>>();
      this.eventMap.set(eventName, eventHandlersSet);
    }

    if (!init?.location) return;

    if (isSignal(init.location) || typeof init.location === 'function') {
      const typedLocation = init.location;
      const location = computed(() => typedLocation());

      effect((onCleanup) => {
        const currentLocation = location();
        if (!currentLocation) return;

        untracked(() => {
          this.connect(
            typeof currentLocation === 'string'
              ? { path: currentLocation }
              : {
                  path: currentLocation?.path,
                  pathParams: currentLocation?.pathParams,
                  queryParams: currentLocation?.queryParams,
                  headers: currentLocation?.headers,
                }
          );
        });

        onCleanup(() => {
          untracked(() => this.disconnect());
        });
      });
    } else {
      this.connect(
        typeof init.location === 'string' ? { path: init.location } : init.location
      );
    }
  }

  connect(location: SseLocation): void {
    if (this.subscription && this.connected()) {
      console.warn('[SseResource] Already connected to location: ', location);
      return;
    }

    console.log('connect: ', location);

    this.subscription = this.sseDataAccess
      .stream({
        path: location.path,
        queryParams: location.queryParams ?? {},
        pathParams: location.pathParams ?? {},
        requestOptions: {
          headers: location.headers ?? {},
        },
      })
      .subscribe((event) => {
        const messageEvent = event as MessageEvent;
        console.info(
          `SSE response with type "${messageEvent.type}" and data "${messageEvent.data} received"`
        );
        const handlers = this.eventMap.get(messageEvent.type);
        if (!handlers) return;

        const msgData = JSON.parse(messageEvent.data);

        this.messages$.next(
          this.messages$.getValue().concat({
            type: messageEvent.type,
            data: msgData,
            lastEventId: messageEvent?.lastEventId,
          })
        );

        handlers.forEach((handler: EventHandler<unknown>) => handler(msgData));
      });

    this.subscription.add(() => this.connected.set(false));
    this.connected.set(true);
  }

  disconnect(): void {
    if (this.subscription || this.connected()) {
      this.subscription?.unsubscribe();
      this.subscription = undefined;
      this.connected.set(false);
      console.log('sse resource: disconnected');
    }
  }

  on<EventName extends keyof EventDefObject>(
    event: EventName,
    handler: EventHandlers<EventDefObject>[EventName],
    options?: SseResourceHandleOptions
  ): HandlerRef {
    if (options && options?.active) {
      const active = isSignal(options.active)
        ? options.active
        : computed(() => options?.active?.() ?? true);

      effect(
        () => {
          if (!options.active) return;
          const isActive = active();
          if (isActive) {
            this.addHandler(event, handler as EventHandler<unknown>);
          } else {
            this.removeHandler(event as string, handler as EventHandler<unknown>);
          }
        },
        { injector: options?.injector ?? this.injector }
      );
    } else {
      this.addHandler(event, handler as EventHandler<unknown>);
    }

    return {
      remove: () => {
        this.removeHandler(event as string, handler as EventHandler<unknown>);
      },
    };
  }

  handle(
    eventHandlerMap: Partial<EventHandlers<EventDefObject>>,
    options?: SseResourceHandleOptions
  ): HandlerRef {
    const addHandlers = () => {
      for (const eventName in eventHandlerMap) {
        if (!eventHandlerMap[eventName]) continue;
        this.addHandler(eventName, eventHandlerMap[eventName] as EventHandler<unknown>);
      }
    };

    if (options && options?.active) {
      const active = isSignal(options.active)
        ? options.active
        : computed(() => options?.active?.() ?? true);

      effect(
        () => {
          if (!options.active) return;
          const isActive = active();
          if (isActive) {
            addHandlers();
          } else {
            this.removeHandlers(eventHandlerMap);
          }
        },
        { injector: this.injector }
      );
    } else {
      addHandlers();
    }

    return {
      remove: () => {
        this.removeHandlers(eventHandlerMap);
      },
    };
  }

  private removeHandlers(eventHandlerMap: Partial<EventHandlers<EventDefObject>>): void {
    for (const eventName in eventHandlerMap) {
      const handler = eventHandlerMap[eventName];
      if (!handler) return;
      this.removeHandler(eventName, handler as EventHandler<unknown>);
    }
  }

  private removeHandler(eventName: string, handler: EventHandler<unknown>): void {
    const handlers = this.eventMap.get(eventName) ?? new Set();
    handlers.delete(handler);
    this.eventMap.set(eventName, new Set(handlers));
  }

  private addHandler<EventName extends keyof EventDefObject = string>(
    eventName: EventName,
    handler: EventHandler<unknown>
  ): void {
    const handlers = this.eventMap.get(eventName) ?? new Set();

    if (handlers.has(handler)) {
      console.warn(
        `[SSE Resource] The handler for event ${String(eventName)} is already registered!`
      );
    } else {
      handlers.add(handler);
    }

    this.eventMap.set(eventName, handlers);
  }
}
